import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import SEOHead from '../common/SEOHead';
import AuthorByline from '../common/AuthorByline';
import { getComparison, ComparisonPage as ComparisonPageData } from '../../data/comparisonContent';
import { getComparisonPageSEO } from '../../utils/seo';
import {
  Reveal,
  SectionDivider,
  PolicyHero,
  MarkdownBody,
  inline,
} from './PolicyPage';

const ComparisonSection: React.FC<{ heading: string; body: string; index: number }> = ({ heading, body, index }) => (
  <section className="relative py-2">
    <div className="flex items-baseline gap-4 mb-5">
      <span
        className="font-accent italic text-xl md:text-2xl text-gold-700 leading-none select-none"
        aria-hidden="true"
      >
        {index}.
      </span>
      <h2 className="font-display text-[26px] sm:text-[32px] text-forest-900 leading-[1.1] tracking-tight">
        {heading}
      </h2>
    </div>
    <div className="pl-0 sm:pl-10">
      <MarkdownBody content={body} />
    </div>
  </section>
);

const FAQRow: React.FC<{ q: string; a: string }> = ({ q, a }) => (
  <div className="bg-cream-50 border border-warm-200 rounded-sm overflow-hidden">
    <div className="px-6 py-5 border-b border-warm-200/60 flex items-start gap-4">
      <span
        className="font-accent italic text-gold-700 leading-none flex-shrink-0 select-none text-[20px]"
        aria-hidden="true"
      >
        ❦
      </span>
      <h3 className="font-display text-[18px] sm:text-[19px] text-forest-900 leading-[1.35]">
        {q}
      </h3>
    </div>
    <div className="px-6 py-5 pl-[3.25rem]">
      <p
        className="text-[15px] text-warm-700 leading-[1.7]"
        dangerouslySetInnerHTML={{ __html: inline(a) }}
      />
    </div>
  </div>
);

const ComparisonPageView: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  if (!slug) return <Navigate to="/" replace />;

  const data: ComparisonPageData | null = getComparison(slug);
  if (!data) return <Navigate to="/" replace />;

  return (
    <>
      <SEOHead seoData={getComparisonPageSEO(data)} />
      <div className="min-h-screen bg-white">
        <PolicyHero
          eyebrow={data.eyebrow}
          title={data.title}
          intro={data.intro}
          lastUpdated={data.lastUpdated}
        />

        <div className="max-w-4xl mx-auto px-6 sm:px-8 py-16 md:py-24">
          {data.sections.map((section, idx) => (
            <Reveal key={idx} delay={idx === 0 ? 0 : 60}>
              {idx > 0 && <SectionDivider />}
              <ComparisonSection heading={section.heading} body={section.body} index={idx + 1} />
            </Reveal>
          ))}

          {data.faq && data.faq.length > 0 && (
            <Reveal delay={80}>
              <SectionDivider />
              <section className="relative py-2">
                <div className="flex items-baseline gap-4 mb-7">
                  <span
                    className="font-accent italic text-xl md:text-2xl text-gold-700 leading-none select-none"
                    aria-hidden="true"
                  >
                    {data.sections.length + 1}.
                  </span>
                  <h2 className="font-display text-[26px] sm:text-[32px] text-forest-900 leading-[1.1] tracking-tight">
                    Common questions
                  </h2>
                </div>
                <div className="pl-0 sm:pl-10 space-y-6">
                  {data.faq.map((q, i) => (
                    <FAQRow key={i} q={q.question} a={q.answer} />
                  ))}
                </div>
              </section>
            </Reveal>
          )}

          {data.productLinks && data.productLinks.length > 0 && (
            <Reveal delay={100}>
              <div className="mt-20 pt-12 border-t border-warm-200">
                <div className="flex items-center justify-center gap-3 mb-6" aria-hidden="true">
                  <div className="h-px bg-warm-300 w-10" />
                  <span className="text-gold-500 text-sm">✦</span>
                  <div className="h-px bg-warm-300 w-10" />
                </div>
                <h3 className="font-display text-[22px] text-forest-900 text-center mb-6">Browse mentioned products</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
                  {data.productLinks.map(link => (
                    <Link
                      key={link.productId}
                      to={`/product/${link.productId}`}
                      className="group flex items-center justify-between gap-3 px-5 py-4 border border-warm-200 rounded-xl hover:border-gold-400 hover:bg-cream-50 transition-colors"
                    >
                      <span className="text-sm font-medium text-forest-800 group-hover:text-forest-900">
                        {link.name}
                      </span>
                      <svg className="w-4 h-4 text-warm-400 group-hover:text-gold-600 transition-colors" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  ))}
                </div>
              </div>
            </Reveal>
          )}

          <AuthorByline lastUpdated={data.lastUpdated} />
        </div>
      </div>
    </>
  );
};

export default ComparisonPageView;
